/**
 * Account settings page for all roles. Renders personal info, a role-specific
 * profile card (founder/eir/ceg), and a security section (password + TOTP MFA).
 *
 * Personal fields and profile fields persist through two different endpoints
 * (updateAccountPersonal vs updateAccountProfile), so they update separate
 * slices of account state. Profile fields live under account.profile.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { getAccount, updateAccountPersonal, updateAccountProfile, changePassword, uploadAvatar, mfaSetup, mfaConfirm, mfaDisable } from '../services/apiService';
import { validateNewPassword, PASSWORD_REQUIREMENTS_TEXT } from '../utils/passwordPolicy';

const STAGE_OPTIONS = [
  { value: 'idea',      label: 'Idea' },
  { value: 'prototype', label: 'Prototype' },
  { value: 'pilot',     label: 'Pilot' },
  { value: 'mvp',       label: 'MVP' },
  { value: 'revenue',   label: 'Revenue' },
  { value: 'scaling',   label: 'Scaling' },
];

const ROLE_LABELS = { founder: 'Founder', eir: 'EIR', ceg: 'CEG Staff', admin: 'Admin' };
const ROLE_COLORS = {
  founder: { background: 'var(--color-mint-dark)', color: 'var(--color-forest)' },
  eir:     { background: '#f0e8ff', color: '#5b21b6' },
  ceg:     { background: '#e0f2fe', color: '#0369a1' },
  admin:   { background: '#fff1ee', color: '#c66b47' },
};

/** Self-service account settings; shows profile fields conditional on role. */
export default function Account() {
  const navigate = useNavigate();
  const { user: authUser, logout } = useAuth();

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    getAccount()
      .then(setAccount)
      .catch(() => setLoadError('Unable to load account. Please refresh.'))
      .finally(() => setLoading(false));
  }, []);

  const savePersonal = useCallback(async (field, value) => {
    const updated = await updateAccountPersonal({ [field]: value });
    setAccount((prev) => ({ ...prev, ...updated }));
  }, []);

  const saveProfile = useCallback(async (field, value) => {
    await updateAccountProfile({ [field]: value });
    setAccount((prev) => ({
      ...prev,
      profile: { ...prev.profile, [field]: value === '' ? null : value },
    }));
  }, []);

  if (loading) return <div style={styles.loading}>Loading…</div>;
  if (loadError) return <div style={styles.loading}>{loadError}</div>;
  if (!account) return null;

  const initials = `${account.first_name?.[0] || ''}${account.last_name?.[0] || ''}`.toUpperCase();
  const p = account.profile || {};

  return (
    <div style={styles.page} className="page-scroll">
      <div style={styles.inner}>

        {/* ── Hero ────────────────────────────────────────────── */}
        <div style={styles.hero}>
          <AvatarUpload
            initials={initials}
            avatarUrl={account.avatar_url}
            onUpload={(url) => setAccount((prev) => ({ ...prev, avatar_url: url }))}
          />
          <div style={styles.heroInfo}>
            <h1 style={styles.heroName}>{account.first_name} {account.last_name}</h1>
            <p style={styles.heroEmail}>{account.email}</p>
            <span style={{ ...styles.rolePill, ...(ROLE_COLORS[account.role] || ROLE_COLORS.founder) }}>
              {ROLE_LABELS[account.role] || account.role}
            </span>
          </div>
        </div>

        {/* ── Personal Information ────────────────────────────── */}
        <Card title="Personal Information">
          <EditableField
            label="First Name"
            value={account.first_name}
            onSave={(v) => savePersonal('first_name', v)}
          />
          <EditableField
            label="Last Name"
            value={account.last_name}
            onSave={(v) => savePersonal('last_name', v)}
          />
          <ReadOnlyField
            label="Email"
            value={account.email}
            note="Contact support to update your email address."
          />
        </Card>

        {/* ── Founder: Company ──────────────────────────────────  */}
        {account.role === 'founder' && (
          <Card title="Company">
            <EditableField label="Name" value={p.company_name} onSave={(v) => saveProfile('company_name', v)} />
            <EditableField
              label="Stage"
              value={p.company_stage}
              type="select"
              options={STAGE_OPTIONS}
              onSave={(v) => saveProfile('company_stage', v)}
            />
            <EditableField label="Industry" value={p.industry} onSave={(v) => saveProfile('industry', v)} />
            <EditableField
              label="About"
              value={p.description}
              type="textarea"
              onSave={(v) => saveProfile('description', v)}
            />
            <EditableField label="Website" value={p.website} placeholder="https://yourcompany.com" onSave={(v) => saveProfile('website', v)} />
            <EditableField label="LinkedIn" value={p.linkedin} placeholder="https://linkedin.com/in/..." onSave={(v) => saveProfile('linkedin', v)} />
            <EditableField label="Founded" value={p.founded_date} placeholder="e.g. 2022" onSave={(v) => saveProfile('founded_date', v)} />
            <EditableField label="Team Size" value={p.team_size != null ? String(p.team_size) : null} type="number" onSave={(v) => saveProfile('team_size', v ? parseInt(v, 10) : null)} />
            <EditableField label="Location" value={p.location} placeholder="City, State" onSave={(v) => saveProfile('location', v)} />
          </Card>
        )}

        {/* ── EIR: Professional ─────────────────────────────────  */}
        {account.role === 'eir' && (
          <Card title="Professional">
            <EditableField label="Bio" value={p.bio} type="textarea" onSave={(v) => saveProfile('bio', v)} />
            <EditableField label="LinkedIn" value={p.linkedin} placeholder="https://linkedin.com/in/..." onSave={(v) => saveProfile('linkedin', v)} />
            <EditableField label="Expertise" value={p.expertise} placeholder="e.g. SaaS, B2B, GTM" onSave={(v) => saveProfile('expertise', v)} />
            <EditableField label="Phone" value={p.phone} placeholder="+1 (555) 000-0000" onSave={(v) => saveProfile('phone', v)} />
            <EditableField label="Booking Link" value={p.booking_link} placeholder="https://calendar.app.google/..." onSave={(v) => saveProfile('booking_link', v)} />
            <EditableField
              label="Visibility"
              value={p.visible_to_all_founders === false ? 'my_founders' : 'all'}
              type="select"
              options={[
                { value: 'all', label: 'All founders' },
                { value: 'my_founders', label: 'My founders only' },
              ]}
              onSave={(v) => saveProfile('visible_to_all_founders', v === 'all')}
            />
          </Card>
        )}

        {/* ── CEG: Professional ─────────────────────────────────  */}
        {account.role === 'ceg' && (
          <Card title="Professional">
            <EditableField label="Title" value={p.title} placeholder="e.g. Program Manager" onSave={(v) => saveProfile('title', v)} />
            <EditableField label="Bio" value={p.bio} type="textarea" onSave={(v) => saveProfile('bio', v)} />
            <EditableField label="LinkedIn" value={p.linkedin} placeholder="https://linkedin.com/in/..." onSave={(v) => saveProfile('linkedin', v)} />
            <EditableField label="Phone" value={p.phone} placeholder="+1 (555) 000-0000" onSave={(v) => saveProfile('phone', v)} />
          </Card>
        )}

        {/* ── Security ─────────────────────────────────────────  */}
        <Card title="Security">
          <PasswordSection />
          <MFASection
            enabled={account.totp_enabled}
            onToggle={(enabled) => setAccount((prev) => ({ ...prev, totp_enabled: enabled }))}
          />
        </Card>

        {/* ── Sign out ─────────────────────────────────────────  */}
        <div style={styles.signOutRow}>
          <button style={styles.signOutBtn} onClick={logout}>Sign out</button>
        </div>

      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────────

function Card({ title, children }) {
  return (
    <div style={cardStyles.wrap}>
      <h2 style={cardStyles.title}>{title}</h2>
      <div style={cardStyles.fields}>{children}</div>
    </div>
  );
}

function ReadOnlyField({ label, value, note }) {
  return (
    <div style={fieldStyles.row}>
      <span style={fieldStyles.label}>{label}</span>
      <div style={{ ...fieldStyles.valueArea, flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
        <span style={fieldStyles.value}>{value || '—'}</span>
        {note && <span style={fieldStyles.note}>{note}</span>}
      </div>
    </div>
  );
}

// Inline edit-in-place field. onSave is awaited; a transient "Saved" badge shows
// on success, and a thrown error is surfaced under the input.
function EditableField({ label, value, onSave, type = 'text', options = [], placeholder = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  function startEdit() {
    setDraft(value || '');
    setEditing(true);
    setError('');
    setSaved(false);
  }

  function cancel() {
    setEditing(false);
    setDraft(value || '');
    setError('');
  }

  async function save() {
    setSaving(true);
    setError('');
    try {
      await onSave(draft);
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const displayValue = type === 'select'
    ? (options.find((o) => o.value === value)?.label || value || '—')
    : (value || '—');

  if (!editing) {
    return (
      <div style={fieldStyles.row}>
        <span style={fieldStyles.label}>{label}</span>
        <div style={fieldStyles.valueArea}>
          <span style={{ ...fieldStyles.value, ...(type === 'textarea' ? fieldStyles.valueMulti : {}) }}>
            {displayValue}
          </span>
          {saved && <span style={fieldStyles.savedBadge}>Saved</span>}
        </div>
        <button style={fieldStyles.editBtn} onClick={startEdit}>Edit</button>
      </div>
    );
  }

  return (
    <div style={{ ...fieldStyles.row, ...fieldStyles.rowEditing }}>
      <span style={fieldStyles.label}>{label}</span>
      <div style={fieldStyles.editArea}>
        {type === 'textarea' ? (
          <textarea
            style={fieldStyles.textarea}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            rows={4}
            autoFocus
          />
        ) : type === 'select' ? (
          <select
            style={fieldStyles.input}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
          >
            <option value="">— Select —</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ) : (
          <input
            style={fieldStyles.input}
            type={type === 'number' ? 'number' : 'text'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter' && type !== 'textarea') save(); if (e.key === 'Escape') cancel(); }}
          />
        )}
        {error && <p style={fieldStyles.fieldError}>{error}</p>}
        <div style={fieldStyles.editActions}>
          <button style={fieldStyles.saveBtn} onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button style={fieldStyles.cancelBtn} onClick={cancel} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Avatar tile with hover overlay; opens AvatarCropModal before uploading. Calls
// bumpAvatar() so the nav avatar busts its cache, and appends a ?t= timestamp to
// the preview URL for the same reason.
function AvatarUpload({ initials, avatarUrl, onUpload }) {
  const { bumpAvatar } = useAuth();
  const [hover, setHover] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [cropSrc, setCropSrc] = useState(null);
  const inputRef = useRef(null);
  const [ts, setTs] = useState(() => Date.now());

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (ev) => setCropSrc(ev.target.result);
    reader.readAsDataURL(file);
  }

  async function handleCropConfirm(blob) {
    setCropSrc(null);
    setUploading(true);
    setError('');
    try {
      const result = await uploadAvatar(blob);
      setTs(Date.now());
      onUpload(result.avatar_url);
      bumpAvatar();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  const showOverlay = hover || uploading;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div
        style={{ ...styles.avatar, position: 'relative', cursor: 'pointer', overflow: 'hidden' }}
        onClick={() => !uploading && inputRef.current?.click()}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl.startsWith('http') ? avatarUrl : `${avatarUrl}?t=${ts}`}
            alt="Profile"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <span style={{ fontFamily: 'var(--font-accent)', fontSize: 26, fontWeight: 800, color: 'var(--color-forest)' }}>
            {initials}
          </span>
        )}

        {showOverlay && (
          <div style={avatarStyles.overlay}>
            {uploading ? (
              <span style={avatarStyles.overlayText}>Saving…</span>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
                  <circle cx="12" cy="13" r="4" stroke="#fff" strokeWidth="2" />
                </svg>
                <span style={avatarStyles.overlayText}>Change</span>
              </>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>

      {error && <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-overdue)', margin: 0 }}>{error}</p>}

      {cropSrc && (
        <AvatarCropModal
          src={cropSrc}
          onConfirm={handleCropConfirm}
          onCancel={() => setCropSrc(null)}
        />
      )}
    </div>
  );
}

// Square crop UI: drag to pan, slider to zoom. Renders the visible region to a
// 300x300 canvas and hands back a JPEG File via onConfirm.
function AvatarCropModal({ src, onConfirm, onCancel }) {
  const CROP_SIZE = 300;
  const [imgNat, setImgNat] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(0.1);
  const [maxScale, setMaxScale] = useState(3);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      // fillScale: image covers circle edge-to-edge — good default for avatars.
      // min is set to half the fit scale so the user can always zoom out to see context.
      const fitScale  = Math.min(CROP_SIZE / img.naturalWidth, CROP_SIZE / img.naturalHeight);
      const fillScale = Math.max(CROP_SIZE / img.naturalWidth, CROP_SIZE / img.naturalHeight);
      const w = img.naturalWidth * fillScale;
      const h = img.naturalHeight * fillScale;
      setImgNat({ w: img.naturalWidth, h: img.naturalHeight });
      setMinScale(fitScale * 0.5);
      setMaxScale(fillScale * 4);
      setScale(fillScale);
      setOffset({ x: (CROP_SIZE - w) / 2, y: (CROP_SIZE - h) / 2 });
    };
    img.src = src;
  }, [src]);

  function clamp(ox, oy, s) {
    const w = imgNat.w * s;
    const h = imgNat.h * s;
    // When image is smaller than the crop area (zoom < fill), allow positive
    // offsets so the image stays centered; otherwise constrain to edges.
    return {
      x: w >= CROP_SIZE ? Math.min(0, Math.max(CROP_SIZE - w, ox)) : (CROP_SIZE - w) / 2,
      y: h >= CROP_SIZE ? Math.min(0, Math.max(CROP_SIZE - h, oy)) : (CROP_SIZE - h) / 2,
    };
  }

  function onMouseDown(e) {
    setDragging(true);
    dragRef.current = { sx: e.clientX - offset.x, sy: e.clientY - offset.y };
  }
  function onMouseMove(e) {
    if (!dragging || !dragRef.current) return;
    setOffset(clamp(e.clientX - dragRef.current.sx, e.clientY - dragRef.current.sy, scale));
  }
  function onMouseUp() { setDragging(false); }

  function onScaleChange(e) {
    const s = parseFloat(e.target.value);
    const cx = CROP_SIZE / 2;
    const cy = CROP_SIZE / 2;
    const imgCx = (cx - offset.x) / scale;
    const imgCy = (cy - offset.y) / scale;
    setOffset(clamp(cx - imgCx * s, cy - imgCy * s, s));
    setScale(s);
  }

  function confirm() {
    const canvas = document.createElement('canvas');
    canvas.width = CROP_SIZE;
    canvas.height = CROP_SIZE;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CROP_SIZE, CROP_SIZE);
      ctx.drawImage(img, offset.x, offset.y, imgNat.w * scale, imgNat.h * scale);
      canvas.toBlob(
        (blob) => onConfirm(new File([blob], 'avatar.jpg', { type: 'image/jpeg' })),
        'image/jpeg', 0.92
      );
    };
    img.src = src;
  }

  return (
    <div style={cropStyles.backdrop} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={cropStyles.modal}>
        <h2 style={cropStyles.title}>Crop Photo</h2>

        <div
          style={{ ...cropStyles.viewport, cursor: dragging ? 'grabbing' : 'grab' }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          {imgNat.w > 0 && (
            <img
              src={src}
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                left: offset.x,
                top: offset.y,
                width: imgNat.w * scale,
                height: imgNat.h * scale,
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>

        <div style={cropStyles.sliderRow}>
          <span style={cropStyles.sliderLabel}>Zoom</span>
          <input
            type="range"
            min={minScale}
            max={maxScale}
            step={minScale * 0.01}
            value={scale}
            onChange={onScaleChange}
            style={cropStyles.slider}
          />
        </div>

        <div style={cropStyles.actions}>
          <button style={cropStyles.cancelBtn} onClick={onCancel}>Cancel</button>
          <button style={cropStyles.applyBtn} onClick={confirm}>Apply</button>
        </div>
      </div>
    </div>
  );
}

const avatarStyles = {
  overlay: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 },
  overlayText: { fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700, color: '#fff', letterSpacing: '0.04em', textTransform: 'uppercase' },
};

const cropStyles = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 },
  modal: { background: '#fff', borderRadius: 16, padding: '28px 28px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, width: 370, boxShadow: '0 24px 48px rgba(0,0,0,0.25)' },
  title: { fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--color-forest)', margin: 0, alignSelf: 'flex-start' },
  viewport: { width: 300, height: 300, borderRadius: '50%', overflow: 'hidden', position: 'relative', background: '#f5f5f5', border: '3px solid var(--color-mint-dark)', flexShrink: 0 },
  sliderRow: { display: 'flex', alignItems: 'center', gap: 12, width: '100%' },
  sliderLabel: { fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--color-forest)', opacity: 0.55, minWidth: 34 },
  slider: { flex: 1, accentColor: 'var(--color-forest)' },
  actions: { display: 'flex', gap: 10, width: '100%' },
  cancelBtn: { flex: 1, padding: '10px', borderRadius: 8, fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: 'none', border: '1.5px solid var(--color-mint-dark)', cursor: 'pointer' },
  applyBtn: { flex: 1, padding: '10px', borderRadius: 8, fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-body)', color: '#fff', background: 'var(--color-forest)', border: 'none', cursor: 'pointer' },
};

function PasswordSection() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  function reset() {
    setCurrent(''); setNext(''); setConfirm('');
    setError(''); setSuccess(false);
    setOpen(false);
  }

  async function handleSave() {
    setError('');
    if (!current) { setError('Enter your current password.'); return; }
    const pwError = validateNewPassword(next, current);
    if (pwError) { setError(pwError); return; }
    if (next !== confirm) { setError('Passwords do not match.'); return; }
    setSaving(true);
    try {
      await changePassword(current, next);
      setSuccess(true);
      setTimeout(reset, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to change password.');
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <div style={fieldStyles.row}>
        <span style={fieldStyles.label}>Password</span>
        <span style={fieldStyles.value}>••••••••••</span>
        <button style={fieldStyles.editBtn} onClick={() => setOpen(true)}>Change</button>
      </div>
    );
  }

  return (
    <div style={{ ...fieldStyles.row, ...fieldStyles.rowEditing, alignItems: 'flex-start' }}>
      <span style={{ ...fieldStyles.label, paddingTop: 8 }}>Password</span>
      <div style={fieldStyles.editArea}>
        <PasswordInput label="Current password" value={current} onChange={setCurrent} show={showCurrent} onToggle={() => setShowCurrent((v) => !v)} />
        <PasswordInput label="New password" value={next} onChange={setNext} show={showNext} onToggle={() => setShowNext((v) => !v)} />
        <PasswordInput label="Confirm new password" value={confirm} onChange={setConfirm} show={showNext} onToggle={() => {}} noToggle />
        <p style={fieldStyles.hint}>{PASSWORD_REQUIREMENTS_TEXT}.</p>
        {error && <p style={fieldStyles.fieldError}>{error}</p>}
        {success && <p style={{ ...fieldStyles.fieldError, color: '#15803d' }}>Password updated!</p>}
        <div style={fieldStyles.editActions}>
          <button style={fieldStyles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button style={fieldStyles.cancelBtn} onClick={reset} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function PasswordInput({ label, value, onChange, show, onToggle, noToggle }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <label style={fieldStyles.pwLabel}>{label}</label>
        {!noToggle && (
          <button type="button" style={fieldStyles.showHideBtn} onClick={onToggle}>
            {show ? 'Hide' : 'Show'}
          </button>
        )}
      </div>
      <input
        style={fieldStyles.input}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="••••••••"
      />
    </div>
  );
}

// TOTP enrollment/disable flow. step drives which sub-view renders; setup shows
// the QR/secret from mfaSetup and requires a confirming code, disable requires a
// current code. onToggle reports the new enabled state up to the account card.
function MFASection({ enabled, onToggle }) {
  const [step, setStep] = useState(null); // null | 'setup' | 'disable'
  const [setupData, setSetupData] = useState(null); // { otpauth_uri, secret }
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  function reset() {
    setStep(null); setSetupData(null); setCode('');
    setError(''); setSuccess('');
  }

  async function startSetup() {
    setLoading(true); setError('');
    try {
      const data = await mfaSetup();
      setSetupData(data);
      setStep('setup');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start setup.');
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm() {
    if (code.length !== 6) { setError('Enter the 6-digit code from your app.'); return; }
    setLoading(true); setError('');
    try {
      await mfaConfirm(code);
      setSuccess('Two-factor authentication enabled.');
      onToggle(true);
      setTimeout(reset, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Try again.');
      setCode('');
    } finally {
      setLoading(false);
    }
  }

  async function handleDisable() {
    if (code.length !== 6) { setError('Enter your current 6-digit code to confirm.'); return; }
    setLoading(true); setError('');
    try {
      await mfaDisable(code);
      setSuccess('Two-factor authentication disabled.');
      onToggle(false);
      setTimeout(reset, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Try again.');
      setCode('');
    } finally {
      setLoading(false);
    }
  }

  // Idle state — not in a flow
  if (!step) {
    return (
      <div style={fieldStyles.row}>
        <span style={fieldStyles.label}>2FA</span>
        <div style={fieldStyles.valueArea}>
          {enabled
            ? <span style={{ ...fieldStyles.value, color: '#15803d', fontWeight: 700 }}>Enabled</span>
            : <span style={fieldStyles.value}>Not enabled</span>}
          {success && <span style={{ ...fieldStyles.savedBadge, marginLeft: 8 }}>{success}</span>}
        </div>
        {enabled
          ? <button style={{ ...fieldStyles.editBtn, color: '#dc2626', borderColor: '#fca5a5' }} onClick={() => { setStep('disable'); setError(''); setCode(''); }}>Disable</button>
          : <button style={fieldStyles.editBtn} disabled={loading} onClick={startSetup}>{loading ? 'Loading…' : 'Set up'}</button>}
      </div>
    );
  }

  // Setup flow — show QR URI and confirmation code input
  if (step === 'setup') {
    return (
      <div style={{ ...fieldStyles.row, ...fieldStyles.rowEditing, alignItems: 'flex-start' }}>
        <span style={{ ...fieldStyles.label, paddingTop: 8 }}>2FA</span>
        <div style={fieldStyles.editArea}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', marginBottom: 8 }}>
            Scan this code in your authenticator app (Google Authenticator, Authy, etc.), then enter the 6-digit code to confirm.
          </p>
          {setupData && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ background: '#fff', padding: 12, borderRadius: 8, border: '1.5px solid #c8e0d0', display: 'inline-flex' }}>
                <QRCodeSVG value={setupData.otpauth_uri} size={180} />
              </div>
              <details style={{ width: '100%' }}>
                <summary style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)', opacity: 0.6, cursor: 'pointer', userSelect: 'none' }}>
                  Can't scan? Enter key manually
                </summary>
                <div style={{ background: '#f0f4f0', borderRadius: 8, padding: '10px 14px', marginTop: 8 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 14, color: 'var(--color-forest)', letterSpacing: '0.1em', wordBreak: 'break-all' }}>{setupData.secret}</p>
                </div>
              </details>
            </div>
          )}
          <input
            style={{ ...fieldStyles.input, letterSpacing: '0.25em', textAlign: 'center', fontSize: 18, marginBottom: 0 }}
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            autoFocus
            autoComplete="one-time-code"
          />
          {error && <p style={fieldStyles.fieldError}>{error}</p>}
          {success && <p style={{ ...fieldStyles.fieldError, color: '#15803d' }}>{success}</p>}
          <div style={fieldStyles.editActions}>
            <button style={fieldStyles.saveBtn} onClick={handleConfirm} disabled={loading || code.length !== 6}>
              {loading ? 'Verifying…' : 'Enable 2FA'}
            </button>
            <button style={fieldStyles.cancelBtn} onClick={reset} disabled={loading}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // Disable flow — confirm with current code
  return (
    <div style={{ ...fieldStyles.row, ...fieldStyles.rowEditing, alignItems: 'flex-start' }}>
      <span style={{ ...fieldStyles.label, paddingTop: 8 }}>2FA</span>
      <div style={fieldStyles.editArea}>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', marginBottom: 8 }}>
          Enter a code from your authenticator app to disable 2FA.
        </p>
        <input
          style={{ ...fieldStyles.input, letterSpacing: '0.25em', textAlign: 'center', fontSize: 18, marginBottom: 0 }}
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          autoFocus
          autoComplete="one-time-code"
        />
        {error && <p style={fieldStyles.fieldError}>{error}</p>}
        {success && <p style={{ ...fieldStyles.fieldError, color: '#15803d' }}>{success}</p>}
        <div style={fieldStyles.editActions}>
          <button style={{ ...fieldStyles.saveBtn, background: '#dc2626' }} onClick={handleDisable} disabled={loading || code.length !== 6}>
            {loading ? 'Disabling…' : 'Disable 2FA'}
          </button>
          <button style={fieldStyles.cancelBtn} onClick={reset} disabled={loading}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────────

const styles = {
  page: { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner: { maxWidth: 960, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' },
  loading: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', opacity: 0.6 },

  hero: { background: '#fff', borderRadius: 'var(--radius-lg)', boxShadow: '0 1px 4px rgba(33,81,45,0.08), 0 4px 16px rgba(33,81,45,0.06)', padding: '28px 32px', display: 'flex', alignItems: 'center', gap: 'var(--space-6)' },
  avatar: { width: 72, height: 72, borderRadius: 'var(--radius-full)', background: '#fff', border: '2px solid var(--color-mint-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  heroInfo: { display: 'flex', flexDirection: 'column', gap: 4 },
  heroName: { fontFamily: 'var(--font-accent)', fontSize: 24, fontWeight: 800, color: 'var(--color-forest)', lineHeight: 1.1 },
  heroEmail: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.6 },
  rolePill: { display: 'inline-block', padding: '3px 12px', borderRadius: 'var(--radius-full)', fontSize: 15, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', alignSelf: 'flex-start', marginTop: 2 },

  signOutRow: { display: 'flex', justifyContent: 'flex-end', paddingBottom: 'var(--space-6)' },
  signOutBtn: { padding: '8px 20px', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--color-overdue)', background: 'none', border: '1.5px solid var(--color-overdue)', cursor: 'pointer', opacity: 0.8 },
};

const cardStyles = {
  wrap: { background: '#fff', borderRadius: 'var(--radius-lg)', boxShadow: '0 1px 4px rgba(33,81,45,0.08), 0 4px 16px rgba(33,81,45,0.06)', overflow: 'hidden' },
  title: { fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.09em', padding: '14px 24px 12px', borderBottom: '1px solid #f0f0f0' },
  fields: { display: 'flex', flexDirection: 'column', padding: '0 24px' },
};

const fieldStyles = {
  row: { display: 'flex', alignItems: 'center', gap: 16, padding: '13px 0', borderBottom: '1px solid #f0f0f0', minHeight: 48 },
  rowEditing: { alignItems: 'flex-start', padding: '14px 12px', background: '#f9fdfb', borderRadius: 'var(--radius-sm)', borderBottom: '1px solid #f0f0f0', margin: '0 -12px' },
  label: { fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--color-forest)', minWidth: 120, flexShrink: 0, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' },
  valueArea: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 },
  value: { fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', flex: 1, minWidth: 0, wordBreak: 'break-word', fontWeight: 500 },
  valueMulti: { whiteSpace: 'pre-wrap', lineHeight: 1.6 },
  note: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.4, fontStyle: 'italic', marginTop: 1 },
  savedBadge: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: '#15803d', background: '#dcfce7', padding: '2px 8px', borderRadius: 'var(--radius-full)', flexShrink: 0 },

  editBtn: { marginLeft: 'auto', padding: '4px 14px', borderRadius: 'var(--radius-full)', fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: 'none', border: '1.5px solid #d0e8d8', cursor: 'pointer', flexShrink: 0, opacity: 0.75 },

  editArea: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0 },
  input: { width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1.5px solid #c8e0d0', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', boxSizing: 'border-box', outline: 'none' },
  textarea: { width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1.5px solid #c8e0d0', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', boxSizing: 'border-box', outline: 'none', resize: 'vertical', lineHeight: 1.5 },
  editActions: { display: 'flex', gap: 8, marginTop: 10 },
  saveBtn: { padding: '7px 20px', borderRadius: 'var(--radius-sm)', fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-body)', color: '#fff', background: 'var(--color-forest)', border: 'none', cursor: 'pointer' },
  cancelBtn: { padding: '7px 14px', borderRadius: 'var(--radius-sm)', fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: 'none', border: '1.5px solid #d0e8d8', cursor: 'pointer' },
  fieldError: { fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-overdue)', margin: '6px 0 0' },
  hint: { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.6, margin: '6px 0 0' },

  pwLabel: { fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--color-forest)', opacity: 0.55 },
  showHideBtn: { background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--color-forest)', opacity: 0.55, padding: 0 },
};
